/**
 * Workspace dashboard.
 *
 * One condensed health view for a data scientist landing in the
 * workspace. Four lenses across the top:
 *
 *   * DATA      — fresh signals / total + 24h ingestion sparkline
 *   * TRAININGS — 7d succeeded vs failed + running pill
 *   * INFERENCE — 24h request count + QPS + hourly sparkline
 *   * SPEND     — credits used this period + burn rate + days left
 *
 * Below the hero strip:
 *   * Inference traffic — 24h area chart with a lime gradient fill
 *   * Training velocity — 7-day stacked bar mini-chart
 *   * Top deployments  — five most-trafficked deployments in 24h
 *   * Recent trainings — five most-recent training runs with status
 *
 * Backed by the single ``GET /v1/dashboard/summary`` endpoint. The
 * page polls every 60s so the user can leave it open as a workspace
 * pulse view.
 */

import React, { useEffect, useMemo, useState } from 'react';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  Box, Typography, Stack, Chip, IconButton, Tooltip, alpha,
  CircularProgress, useTheme, Skeleton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BoltIcon from '@mui/icons-material/Bolt';
import StorageIcon from '@mui/icons-material/Storage';
import PaymentsIcon from '@mui/icons-material/Payments';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { DashboardIcon, SegmentsIcon } from '../global/CustomIcons';
import TableChartIcon from '@mui/icons-material/TableChart';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import ApiIcon from '@mui/icons-material/Api';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CableIcon from '@mui/icons-material/Cable';
import HubIcon from '@mui/icons-material/Hub';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import GettingStartedChecklist from './GettingStartedChecklist';
import GoalsHero from './components/GoalsHero';
import { fetchRequest as fetchSummary } from '../../redux/actions/dashboard/summary';
import { fetchRequest as goalsFetch } from '../../redux/actions/discovery/goals';
import {
  Surface,
  KPICard,
  ShortcutButton,
  AreaChart24h,
  StackedDayBars,
  StatusDot,
  formatNumber,
  formatDuration,
  formatRelativeTime,
} from './components/DashboardPrimitives';

const DashboardPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const summary  = useSelector((s) => s.dashboard_summary?.response);
  const loading  = useSelector((s) => s.dashboard_summary?.isLoading);
  const error    = useSelector((s) => s.dashboard_summary?.error);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Goals lead the dashboard. Prefer the summary's rich block (includes the
  // champion's accuracy/skill); fall back to the discovery goals listing so
  // the hero renders identically against an older backend.
  const goalsRedux = useSelector((s) => s.discovery_goals?.response);
  const goalsLoading = useSelector((s) => Boolean(s.discovery_goals?.isLoading));
  const goalsData = useMemo(() => {
    const rich = summary?.goals?.items;
    if (Array.isArray(rich)) return rich;
    if (Array.isArray(goalsRedux)) {
      return goalsRedux.map((g) => ({
        goal_id:           g.goal_id,
        name:              g.name,
        goal_type:         g.goal_type,
        goal_kind:         g.goal_kind || 'signal',
        member_count:      g.fleet?.member_count,
        fleet_model:       g.fleet?.model?.slug,
        target_key:        g.target_key,
        status:            g.status || 'idle',
        shock_state:       g.shock_state || 'normal',
        champion_pipeline_id: g.champion_pipeline_id,
        accuracy:          null,
        skill:             null,
        drivers:           g.edges_count ?? 0,
        horizon_bins:      g.horizon_bins,
        interval_seconds:  g.interval_seconds,
        live_data:         (g.options?.live) !== false,
        next_serve_at:     g.next_serve_at,
      }));
    }
    return null;
  }, [summary, goalsRedux]);

  const { activeWorkspaceId } = useWorkspace();

  const reload = () => {
    dispatch(fetchSummary());
    dispatch(goalsFetch());
    setLastUpdate(new Date());
  };

  // Only fetch once a workspace is active, and re-fetch when it changes.
  // No active workspace -> no call (the onboarding prompt covers that state).
  useEffect(() => {
    if (!activeWorkspaceId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, activeWorkspaceId]);

  // Live (no polling): the summary aggregates ingestion / models / training /
  // deployments / signals / goals — so refresh it when any of those report a
  // change on the workspace channel (and on reconnect). Replaces the 60s poll.
  useRealtimeRefresh(
    eventPrefix('ingestion.', 'training.', 'deployment.', 'signal.analysis.', 'goal.'),
    () => { if (activeWorkspaceId) reload(); },
  );

  const isInitialLoading = loading && !summary;

  // Funnel stage: does this workspace have any data yet? Drives the goal
  // hero's empty state so its CTA is always the *correct* next action.
  const hasData = (summary?.sources?.total || 0) > 0
    || (summary?.data?.total_signals || 0) > 0;

  return (
    <Box className="app-dashboard" sx={{ pb: 6 }}>
      <Box>
        <DashboardHeader
          summary={summary}
          loading={loading}
          lastUpdate={lastUpdate}
          onRefresh={reload}
        />

        <GettingStartedChecklist summary={summary} goals={goalsData} />

        {isInitialLoading ? (
          <LoadingSkeleton />
        ) : error ? (
          <Surface sx={{ p: 4, textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 36, color: 'error.main', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Couldn't load dashboard summary.
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {String(error)}
            </Typography>
          </Surface>
        ) : (
          <>
            {/* Goals first — the workspace's outcome targets get top billing. */}
            <GoalsHero
              goals={goalsData}
              loading={goalsLoading || loading}
              navigate={navigate}
              hasData={hasData}
            />
            <SectionLabel>Workspace health</SectionLabel>
            <KPIRow summary={summary} navigate={navigate} />
            <IngestionFlow summary={summary} navigate={navigate} />
            <ChartsRow summary={summary} navigate={navigate} />
            <PanelsRow summary={summary} navigate={navigate} />
          </>
        )}
      </Box>
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const DashboardHeader = ({ summary, loading, lastUpdate, onRefresh }) => {
  const workspace = summary?.workspace;
  const pipelines = summary?.pipelines || {};
  const spend     = summary?.spend || {};

  // Compact spend summary for the header (the dedicated spend box is
  // gone; budget stays glanceable here without cluttering the KPIs).
  const spendLabel = spend.current_balance != null
    ? `${formatNumber(spend.current_balance)} credits` +
      (spend.days_remaining ? ` · ~${spend.days_remaining}d left` : '')
    : null;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 2.5 }}
    >
      <Box>
        {/* Title matches the breadcrumb header used across the app. */}
        <PageBreadcrumbs
          items={[{ text: 'Dashboard', icon: <DashboardIcon /> }]}
          mb={0.5}
        />
        {/* Workspace context as a compact sub-line. */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ ml: 1 }}
        >
          {workspace?.name && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {workspace.name}
            </Typography>
          )}
          {pipelines.in_training > 0 && (
            <Chip
              size="small"
              icon={<HourglassTopIcon sx={{ fontSize: 14 }} />}
              label={`${pipelines.in_training} training`}
              variant="outlined"
              color="primary"
              sx={{ fontWeight: 500 }}
            />
          )}
          {spendLabel && (
            <Chip
              size="small"
              icon={<PaymentsIcon sx={{ fontSize: 14 }} />}
              label={spendLabel}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
          {workspace?.data_granularity && (
            <Typography variant="caption" color="text.secondary">
              · {workspace.data_granularity} cadence
            </Typography>
          )}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {lastUpdate ? `Updated ${formatRelativeTime(lastUpdate)}` : '—'}
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={loading}>
              {loading
                ? <CircularProgress size={16} />
                : <RefreshIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
};


// ---------------------------------------------------------------------------
// KPI row (four cards)
// ---------------------------------------------------------------------------

const KPIRow = ({ summary, navigate }) => {
  const data       = summary?.data || {};
  const segments   = summary?.segments || {};
  const pipelines  = summary?.pipelines || {};
  const models     = summary?.models || {};
  const trainings  = summary?.trainings || {};

  // Data health colour — green when 90%+ signals fresh, yellow 50-90%, red <50%.
  const freshRatio = data.total_signals > 0 ? data.fresh_signals / data.total_signals : 1;
  const dataTone =
    data.total_signals === 0 ? 'neutral' :
    freshRatio >= 0.9        ? 'success' :
    freshRatio >= 0.5        ? 'warning' : 'error';

  // Training health — failure rate over last 7d.
  const trainTotal = trainings.total_7d || 0;
  const trainFailRate = trainTotal > 0 ? (trainings.failed_7d || 0) / trainTotal : 0;
  const trainingTone =
    trainTotal === 0       ? 'neutral' :
    trainFailRate <= 0.1   ? 'success' :
    trainFailRate <= 0.25  ? 'warning' : 'error';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: 2,
        mb: 2,
      }}
    >
      {/* DATA — distinct signals + freshness + volume */}
      <KPICard
        tone={dataTone}
        icon={<StorageIcon sx={{ fontSize: 18 }} />}
        label="Data Signals"
        onShortcut={() => navigate('/signals')}
        shortcutTitle="Open Data Signals"
        primary={
          <>
            <span style={{ fontWeight: 700 }}>{formatNumber(data.total_signals)}</span>
            <span style={{ opacity: 0.55, fontSize: '0.5em', marginLeft: 6 }}>
              distinct
            </span>
          </>
        }
        subtitle={`${formatNumber(data.fresh_signals)} fresh · ${formatNumber(data.signals_total)} signals total`}
        footer={data.stale_signals?.length > 0 ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            <Typography variant="caption" color="text.secondary">
              {data.stale_signals.length} stale signal
              {data.stale_signals.length === 1 ? '' : 's'}
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">
              All signals current
            </Typography>
          </Stack>
        )}
      />

      {/* DATA — segments (slices the pipelines train on) */}
      <KPICard
        tone="neutral"
        icon={<SegmentsIcon sx={{ fontSize: 18 }} />}
        label="Segments"
        onShortcut={() => navigate('/segments')}
        shortcutTitle="Open Segments"
        primary={<span style={{ fontWeight: 700 }}>{formatNumber(segments.total)}</span>}
        subtitle="Feature/label slices your pipelines train on"
      />

      {/* MODELING — pipelines + the reusable models behind them */}
      <KPICard
        tone="neutral"
        icon={<AccountTreeIcon sx={{ fontSize: 18 }} />}
        label="Pipelines"
        onShortcut={() => navigate('/pipelines')}
        shortcutTitle="Open Pipelines"
        primary={<span style={{ fontWeight: 700 }}>{formatNumber(pipelines.total)}</span>}
        subtitle={`${formatNumber(models.total)} model${models.total === 1 ? '' : 's'} · ${
          formatNumber(pipelines.active)} active`}
        footer={pipelines.in_training > 0 ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PulsingDot color="primary.main" />
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
              {pipelines.in_training} training now
            </Typography>
          </Stack>
        ) : null}
      />

      {/* MODELING — training factory health (7d) */}
      <KPICard
        tone={trainingTone}
        icon={<AutoGraphIcon sx={{ fontSize: 18 }} />}
        label="Trainings · 7d"
        onShortcut={() => navigate('/trainings')}
        shortcutTitle="Open Trainings"
        primary={
          <>
            <span style={{ color: 'inherit', fontWeight: 700 }}>
              {trainings.succeeded_7d || 0}
            </span>
            <span style={{ opacity: 0.4, fontSize: '0.55em', margin: '0 4px' }}>/</span>
            <span style={{
              color: (trainings.failed_7d || 0) > 0 ? '#d83f3f' : 'inherit',
              fontWeight: 700,
            }}>
              {trainings.failed_7d || 0}
            </span>
            <span style={{ opacity: 0.55, fontSize: '0.5em', marginLeft: 6 }}>
              ok / fail
            </span>
          </>
        }
        subtitle={`avg ${formatDuration(trainings.avg_duration_seconds)} · ${trainTotal} run${
          trainTotal === 1 ? '' : 's'}`}
        sparkline={<StackedDayBars data={trainings.daily || []} />}
        footer={trainings.running > 0 ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PulsingDot color="primary.main" />
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
              {trainings.running} active now
            </Typography>
          </Stack>
        ) : null}
      />
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Ingestion flow — full-width row: data sources (left) streaming into the
// workspace hub (right), with animated flow lanes per source.
// ---------------------------------------------------------------------------

const CONNECTOR_ICONS = {
  bigquery:  TableChartIcon,
  postgres:  StorageIcon,
  mysql:     StorageIcon,
  sqlserver: StorageIcon,
  s3:        CloudQueueIcon,
  gcs:       CloudQueueIcon,
  rest:      ApiIcon,
  rest_api:  ApiIcon,
  api:       ApiIcon,
  webhook:   ApiIcon,
  kafka:     BoltIcon,
  file:      InsertDriveFileIcon,
  csv:       InsertDriveFileIcon,
  upload:    InsertDriveFileIcon,
};

const connectorIcon = (type) =>
  CONNECTOR_ICONS[String(type || '').toLowerCase()] || CableIcon;

// Health -> { tone, label, flowing }. ``flowing`` drives the animated
// lane: only actively-healthy / streaming sources animate.
const SOURCE_HEALTH = {
  running: { tone: 'primary', label: 'Streaming',  flowing: true  },
  healthy: { tone: 'success', label: 'Healthy',    flowing: true  },
  error:   { tone: 'error',   label: 'Failed',     flowing: false },
  stale:   { tone: 'warning', label: 'Stale',      flowing: false },
  idle:    { tone: 'neutral', label: 'Idle',       flowing: false },
  paused:  { tone: 'neutral', label: 'Paused',     flowing: false },
};

const flowToneColor = (theme, tone) => {
  switch (tone) {
    case 'success': return theme.palette.success.main;
    case 'warning': return theme.palette.warning?.main || '#f59e0b';
    case 'error':   return theme.palette.error.main;
    case 'neutral': return alpha(theme.palette.text.primary, 0.35);
    default:        return theme.palette.primary.main;
  }
};

const MAX_SOURCES_SHOWN = 6;

const FlowLane = ({ tone, flowing }) => {
  const theme = useTheme();
  const color = flowToneColor(theme, tone);
  return (
    <Box sx={{ position: 'relative', flex: 1, minWidth: 36, height: 16 }}>
      {/* dashed track; animates rightward when the source is flowing */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 8,
          height: 2,
          transform: 'translateY(-50%)',
          borderRadius: 2,
          backgroundImage: `linear-gradient(90deg, ${color} 0 7px, transparent 7px 14px)`,
          backgroundSize: '14px 2px',
          backgroundRepeat: 'repeat-x',
          opacity: flowing ? 0.95 : 0.3,
          animation: flowing ? 'ingestFlow 0.7s linear infinite' : 'none',
          '@keyframes ingestFlow': { to: { backgroundPositionX: '14px' } },
        }}
      />
      {/* arrowhead into the hub */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: '4px solid transparent',
          borderBottom: '4px solid transparent',
          borderLeft: `6px solid ${color}`,
          opacity: flowing ? 0.95 : 0.45,
        }}
      />
    </Box>
  );
};

const SourceNode = ({ source }) => {
  const theme = useTheme();
  const meta = SOURCE_HEALTH[source.health] || SOURCE_HEALTH.idle;
  const color = flowToneColor(theme, meta.tone);
  const Icon = connectorIcon(source.connector_type);

  const statusText = (() => {
    if (source.health === 'running') return 'Streaming…';
    if (source.health === 'error')   return 'Last run failed';
    if (source.health === 'paused')  return 'Paused';
    if (source.health === 'idle')    return 'Never run';
    if (source.rows_24h > 0)         return `${formatNumber(source.rows_24h)} rows · 24h`;
    if (source.last_run)             return formatRelativeTime(new Date(source.last_run));
    return meta.label;
  })();

  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
      {/* connector glyph */}
      <Box
        sx={{
          width: 34, height: 34, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 1.5,
          bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.16 : 0.1),
          color,
          position: 'relative',
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
        <Box sx={{ position: 'absolute', bottom: -2, right: -2 }}>
          <StatusDot tone={meta.tone} size={8} />
        </Box>
      </Box>

      {/* name + status */}
      <Box sx={{ minWidth: 0, width: 132 }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.2 }}>
          {source.name}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{ display: 'block', color: meta.tone === 'neutral' ? 'text.secondary' : color, fontWeight: 500 }}
        >
          {statusText}
        </Typography>
      </Box>

      {/* animated lane toward the hub */}
      <FlowLane tone={meta.tone} flowing={meta.flowing} />
    </Stack>
  );
};

const IngestionFlow = ({ summary, navigate }) => {
  const theme = useTheme();
  const sources = summary?.sources || {};
  const data = summary?.data || {};
  const items = sources.items || [];
  const shown = items.slice(0, MAX_SOURCES_SHOWN);
  const overflow = items.length - shown.length;

  return (
    <Surface sx={{ p: 3, mb: 2 }}>
      <ShortcutButton onClick={() => navigate('/datasources')} title="Open Data Sources" />
      <Box sx={{ mb: 2, pr: 4 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1, fontSize: 10 }}>
          Data ingestion
        </Typography>
        <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2, mt: 0.25 }}>
          {sources.total > 0
            ? `${sources.healthy} of ${sources.total} source${sources.total === 1 ? '' : 's'} streaming`
            : 'Live sources'}
        </Typography>
      </Box>

      {items.length === 0 ? (
        <EmptyHint
          icon={<CableIcon sx={{ fontSize: 28 }} />}
          title="No data sources yet"
          subtitle="Connect a source to start pushing data into this workspace."
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            gap: { xs: 2, md: 0 },
          }}
        >
          {/* LEFT — sources, each with its own flow lane pointing right */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.25 }}>
            {shown.map((s) => (
              <SourceNode key={s.source_id} source={s} />
            ))}
            {overflow > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: '46px', cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
                onClick={() => navigate('/datasources')}
              >
                +{overflow} more source{overflow === 1 ? '' : 's'}
              </Typography>
            )}
          </Box>

          {/* RIGHT — the workspace hub all lanes converge into */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', md: 'center' },
              flexShrink: 0,
              width: { xs: '100%', md: 220 },
              // a hairline "bus" the lanes feed into (md+ only)
              borderLeft: { md: '1px dashed' },
              borderColor: { md: 'divider' },
              pl: { md: 3 },
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                px: 2.5,
                py: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.4),
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.05),
                boxShadow: `0 0 32px -8px ${alpha(theme.palette.primary.main, 0.5)}`,
              }}
            >
              <Box
                sx={{
                  width: 44, height: 44, mx: 'auto', mb: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                  color: 'primary.main',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                    animation: 'hubPulse 2.4s ease-out infinite',
                  },
                  '@keyframes hubPulse': {
                    '0%':   { transform: 'scale(1)',   opacity: 0.6 },
                    '100%': { transform: 'scale(1.7)', opacity: 0 },
                  },
                }}
              >
                <HubIcon sx={{ fontSize: 22 }} />
              </Box>
              <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                {summary?.workspace?.name || 'Workspace'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {formatNumber(data.total_signals)} signals · {formatNumber(data.signals_24h)} / 24h
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Surface>
  );
};


// ---------------------------------------------------------------------------
// Charts row — ingestion (data in) + inference (predictions out), side by side
// ---------------------------------------------------------------------------

const ChartsRow = ({ summary, navigate }) => {
  const data       = summary?.data || {};
  const inferences = summary?.inferences || {};

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 2,
        mb: 2,
      }}
    >
      {/* Ingestion — signals/hour (data flowing in) */}
      <TrafficChart
        overline="Ingestion"
        title="Last 24 hours"
        onOpen={() => navigate('/signals')}
        openTitle="Open Data Signals"
        series={data.ingestion_hourly || []}
        pills={[
          { label: 'Signals', value: formatNumber(data.signals_24h) },
          { label: 'Avg/hr', value: formatNumber(data.avg_signals_per_hour), accent: true },
        ]}
        emptyTitle="No ingestion yet"
        emptySubtitle="Connect a data source to start ingesting signals."
      />

      {/* Inference — requests/hour (predictions going out) */}
      <TrafficChart
        overline="Inference"
        title="Last 24 hours"
        onOpen={() => navigate('/inferences')}
        openTitle="Open Inferences"
        series={inferences.hourly || []}
        pills={[
          { label: 'Requests', value: formatNumber(inferences.count_24h) },
          { label: 'QPS', value: (inferences.qps_estimate || 0).toFixed(2), accent: true },
        ]}
        emptyTitle="No inference traffic"
        emptySubtitle="Promote a model to a deployment to serve predictions."
      />
    </Box>
  );
};

const TrafficChart = ({
  overline, title, onOpen, openTitle, series, pills,
  emptyTitle, emptySubtitle,
}) => {
  const hasData = Array.isArray(series) && series.some((p) => (p.count || 0) > 0);
  return (
    <Surface sx={{ p: 3, minHeight: 280 }}>
      <ShortcutButton onClick={onOpen} title={openTitle} />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 2, pr: 4 }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1, fontSize: 10 }}>
            {overline}
          </Typography>
          <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2, mt: 0.25 }}>
            {title}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {pills.map((p) => (
            <MetricPill key={p.label} label={p.label} value={p.value} accent={p.accent} />
          ))}
        </Stack>
      </Stack>

      {hasData ? (
        <Box sx={{ height: 200 }}>
          <AreaChart24h data={series} />
        </Box>
      ) : (
        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyHint
            icon={<BoltIcon sx={{ fontSize: 28 }} />}
            title={emptyTitle}
            subtitle={emptySubtitle}
          />
        </Box>
      )}
    </Surface>
  );
};


// ---------------------------------------------------------------------------
// Deployments panel — counts, top by traffic
// ---------------------------------------------------------------------------

const DeploymentsPanel = ({ deployments, onOpen }) => {
  const top = deployments?.top || [];
  const max24h = top.reduce((m, d) => Math.max(m, d.count_24h || 0), 0);

  return (
    <Surface sx={{ p: 3, minHeight: 300 }}>
      <ShortcutButton onClick={onOpen} title="Open Deployments" />
      <Box sx={{ mb: 2, pr: 4 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1, fontSize: 10 }}>
          Deployments
        </Typography>
        <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2, mt: 0.25 }}>
          Serving status
        </Typography>
      </Box>

      {/* Headline counts */}
      <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: 'Lexend Variable, Arial', fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
            {formatNumber(deployments?.active)}
          </Typography>
          <Typography variant="caption" color="text.secondary">active</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontFamily: 'Lexend Variable, Arial', fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
            {formatNumber(deployments?.total)}
          </Typography>
          <Typography variant="caption" color="text.secondary">total</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontFamily: 'Lexend Variable, Arial', fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
            {formatNumber(deployments?.inferences_24h)}
          </Typography>
          <Typography variant="caption" color="text.secondary">calls · 24h</Typography>
        </Box>
      </Stack>

      {/* Top by traffic */}
      {top.length === 0 ? (
        <EmptyHint
          icon={<RocketLaunchIcon sx={{ fontSize: 28 }} />}
          title="No traffic yet"
          subtitle="Promote a model to start serving inferences."
        />
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
            Top by traffic · 24h
          </Typography>
          <Stack spacing={1.25}>
            {top.map((d) => {
              const ratio = max24h > 0 ? (d.count_24h / max24h) : 0;
              const isHealthy = !d.status
                || ['running', 'active', 'ready', 'promoted', 'serving'].includes(d.status);
              return (
                <Box key={d.deployment_id}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                      <StatusDot tone={isHealthy ? 'success' : 'error'} />
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {d.deployment_type ? d.deployment_type.replace(/_/g, ' ') : 'deployment'}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={600}>
                      {formatNumber(d.count_24h)}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 4,
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${Math.max(2, ratio * 100)}%`,
                        bgcolor: 'primary.main',
                        borderRadius: 4,
                        transition: 'width 600ms ease',
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </>
      )}
    </Surface>
  );
};


// ---------------------------------------------------------------------------
// Panels row — serving (deployments) + modeling activity (recent trainings)
// ---------------------------------------------------------------------------

const PanelsRow = ({ summary, navigate }) => {
  const trainings   = summary?.trainings || {};
  const deployments = summary?.deployments || {};

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 2,
      }}
    >
      <DeploymentsPanel
        deployments={deployments}
        onOpen={() => navigate('/deployments')}
      />

      <RecentTrainingsPanel
        items={trainings.recent || []}
        onOpen={() => navigate('/trainings')}
      />
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Recent trainings list
// ---------------------------------------------------------------------------

const RecentTrainingsPanel = ({ items, onOpen }) => (
  <Surface sx={{ p: 3 }}>
    <ShortcutButton onClick={onOpen} title="Open Trainings" />
    <Box sx={{ mb: 2, pr: 4 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1, fontSize: 10 }}>
        Recent trainings
      </Typography>
      <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2, mt: 0.25 }}>
        Latest 5 runs
      </Typography>
    </Box>

    {items.length === 0 ? (
      <EmptyHint
        icon={<AutoGraphIcon sx={{ fontSize: 28 }} />}
        title="No trainings yet"
        subtitle="Start a model training to see runs here."
      />
    ) : (
      <Stack spacing={0.5}>
        {items.map((t) => {
          const tone =
            ['success', 'completed'].includes(t.status) ? 'success' :
            ['error', 'failed', 'cancelled'].includes(t.status) ? 'error' :
            'primary';
          const Icon =
            tone === 'success' ? CheckCircleIcon :
            tone === 'error'   ? ErrorOutlineIcon :
            HourglassTopIcon;
          return (
            <Box
              key={t.uid}
              sx={{
                py: 1,
                px: 1,
                borderRadius: 1.5,
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.04),
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Icon
                  sx={{
                    fontSize: 18,
                    color: `${tone}.main`,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {t.blueprint || t.model_id || t.uid?.slice(0, 8)}
                    </Typography>
                    {t.duration > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        · {formatDuration(t.duration)}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {t.created_at ? formatRelativeTime(new Date(t.created_at)) : '—'} · {t.status}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    )}
  </Surface>
);


// ---------------------------------------------------------------------------
// Misc primitives used inline
// ---------------------------------------------------------------------------

/* Slim section divider so the goal hero reads as the page's headline and
   everything below it as supporting workspace telemetry. */
const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: 'block',
      fontSize: 10,
      letterSpacing: 1,
      fontWeight: 700,
      color: 'text.secondary',
      mb: 1.25,
      mt: 0.5,
    }}
  >
    {children}
  </Typography>
);

const MetricPill = ({ label, value, accent }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.5,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: accent ? 'primary.main' : 'divider',
      bgcolor: (t) => accent
        ? alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.16 : 0.08)
        : 'transparent',
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1, fontSize: 9, letterSpacing: 0.5 }}>
      {label.toUpperCase()}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: accent ? 'primary.main' : 'text.primary',
        fontSize: 14,
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const PulsingDot = ({ color = 'primary.main' }) => (
  <Box
    sx={{
      position: 'relative',
      width: 8,
      height: 8,
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        bgcolor: color,
      },
      '&::after': {
        animation: 'dashboardPulse 1.6s ease-out infinite',
      },
      '@keyframes dashboardPulse': {
        '0%':   { transform: 'scale(1)', opacity: 0.6 },
        '100%': { transform: 'scale(2.6)', opacity: 0 },
      },
    }}
  />
);

const EmptyHint = ({ icon, title, subtitle }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Box sx={{ color: 'text.disabled', mb: 1 }}>{icon}</Box>
    <Typography variant="body2" fontWeight={500} sx={{ mb: 0.25 }}>
      {title}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {subtitle}
    </Typography>
  </Box>
);


// ---------------------------------------------------------------------------
// Loading skeleton + ambient backdrop
// ---------------------------------------------------------------------------

const LoadingSkeleton = () => (
  <>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 2,
        mb: 3,
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rounded" height={170} sx={{ borderRadius: 2 }} />
      ))}
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 3 }}>
      <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <Skeleton variant="rounded" height={240} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rounded" height={240} sx={{ borderRadius: 2 }} />
    </Box>
  </>
);

export default DashboardPage;
